import React from 'react';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { Link } from 'react-router-dom';
import { HeaderClient } from './header-client';
import { LanguageSwitcher } from '@/components/shared/language-switcher';
import { HeaderUserMenu } from '@/components/layout/navbar/user-menu';
import { NotificationBell } from '@/features/notifications';
import { ReportBugTrigger } from '@/features/bug-reports';
import { CommandCenterTrigger } from '@/features/command-center';
// Direct file import — the `@/features/voice` barrel re-exports lazy pages
// and would pull them into the main bundle (same rule as app-layout).
import { CallHeaderBadge } from '@/features/voice/components/call-widget/call-header-badge';
// Direct file import for the same bundle reason as the voice badge above.
import { TrialHeaderChip } from '@/features/billing/components/trial-header-chip';

export interface BreadcrumbItemData {
    href?: string;
    label: string;
    active?: boolean;
}

interface HeaderProps {
    breadcrumbs?: BreadcrumbItemData[];
    /** Page-supplied left content (e.g. the inbox title + unread pill). */
    leftContent?: React.ReactNode;
    rightContent?: React.ReactNode;
}

export function Header({ breadcrumbs, leftContent, rightContent }: HeaderProps) {
    return (
        <header className="sticky top-0 shrink-0 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-16 bg-background z-50 border-b">
            {/* Main navigation row - always visible */}
            <div className="flex h-16 items-center justify-between">
                <div className="flex items-center gap-2 px-4 flex-1">
                    <SidebarTrigger className="-ml-1" />
                    {leftContent ? (
                        <div className="flex items-center gap-2">{leftContent}</div>
                    ) : null}
                    {breadcrumbs?.length ? (
                        <>
                            <Separator orientation="vertical" className="mr-2 h-4" />
                            <Breadcrumb>
                                <BreadcrumbList>
                                    {breadcrumbs.map((crumb, index) => {
                                        const isLast = index === breadcrumbs.length - 1;

                                        if (isLast) {
                                            return (
                                                <BreadcrumbItem key={crumb.label + index}>
                                                    <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                                                </BreadcrumbItem>
                                            );
                                        }

                                        return (
                                            <React.Fragment key={crumb.label + index}>
                                                <BreadcrumbItem className="hidden md:flex">
                                                    <BreadcrumbLink asChild>
                                                        <Link to={crumb.href || '#'}>
                                                            {crumb.label}
                                                        </Link>
                                                    </BreadcrumbLink>
                                                </BreadcrumbItem>
                                                <BreadcrumbSeparator />
                                            </React.Fragment>
                                        );
                                    })}
                                </BreadcrumbList>
                            </Breadcrumb>
                        </>
                    ) : null}
                </div>

                {/* Desktop right content - same line */}
                <HeaderClient rightContent={rightContent} mode="desktop">
                    <TrialHeaderChip />
                    <CommandCenterTrigger />
                    <CallHeaderBadge />
                    <NotificationBell />
                    <ReportBugTrigger />
                    <LanguageSwitcher />
                    <HeaderUserMenu />
                </HeaderClient>
            </div>

            {/* Mobile right content - next line */}
            <HeaderClient rightContent={rightContent} mode="mobile">
                <TrialHeaderChip />
                <CommandCenterTrigger />
                <CallHeaderBadge />
                <NotificationBell />
                <ReportBugTrigger />
                <LanguageSwitcher />
                <HeaderUserMenu />
            </HeaderClient>
        </header>
    );
}
